import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux';
import WalletConnectComponent from './../WalletConnectComponent';
import uuid from 'react-uuid';
import * as signalR from '@microsoft/signalr'
import ApiService from '../Services/ApiService';
import AlertService from '../Services/AlertService';
import Main from '../modules/main/_Main';
import { TRON_PLATFORM_TYPE } from '../Constants/MainKeys';
import { addPageSpinner, removePageSpinner } from '../Store/Reducers/spinnersReducer';
import { setToken } from '../Store/Reducers/mainReducer';
import { useParams } from 'react-router-dom';
import Footer from '../modules/Footer';
import TronComponent from '../TronComponent';
import { cleanupStorageByPlatform } from '../utils/walletCleanup';
import { isPaymentReturnVisit, registerPaymentSeenOnLeave } from '../utils/paymentVisitStorage';
import PaymentDataComponent from '../PaymentDataComponent.jsx';

export default function Home() {

  const { token } = useParams();
  const API_URL_KEY = import.meta.env.VITE_API_URL_KEY || "";
  const dispatch = useDispatch();
  const [globalConnection, setGlobalConnection] = useState(null);
  const [cryllexChains, setCryllexChains] = useState([]);

  const [selectedChain, setSelectedChain] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const previousChainRef = useRef(null);

  const [countries, setCountries] = useState([]);
  const [activeTab, setActiveTab] = useState(1);

  const connectionRef = useRef(null);
  const isStartingRef = useRef(false);

  useEffect(() => {
    getCountries();
    getChains();
    return () => {
      setCountries([]);
      setCryllexChains([]);
    }
  }, [])

  const getCountries = () => {
    ApiService.getCountries().then(response => {
      if (response && response.data) {
        setCountries(response.data);
      }
    }).catch(error => AlertService.alert("error", error));
  }

  const getChains = () => {
    const spinnerId = uuid();
    dispatch(addPageSpinner(spinnerId));
    ApiService.getChains().then(response => {
      if (response && response.data && response.data.data) {
        setCryllexChains(response.data.data)
      }
    }).catch(error => AlertService.alert("error", error)).finally(() => {
      dispatch(removePageSpinner(spinnerId));
    })
  }

  useEffect(() => {
    if (!token) return;

    const isReturnVisit = isPaymentReturnVisit(token);

    // First visit only: clear stale wallet session from other payments
    if (!isReturnVisit) {
      localStorage.removeItem("wagmi.store");
      localStorage.removeItem("wagmi.recentConnectorId");
      localStorage.removeItem("@w3m/connected_connector");
      indexedDB.deleteDatabase("WALLET_CONNECT_V2_INDEXED_DB");
      sessionStorage.clear();
      deleteAllCookies();
    }

    return registerPaymentSeenOnLeave(token);
  }, [token])

  useEffect(() => {
    if (!token) return;
    dispatch(setToken(token));
    signal_start();
    return () => {
      stopConnection();
    };
  }, [token])

  const deleteAllCookies = () => {
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
    });
  }

  const stopConnection = async () => {
    const conn = connectionRef.current;
    connectionRef.current = null;
    setGlobalConnection(null);
    if (!conn) return;
    try {
      await conn.stop();
    } catch { /* ignore */ }
  };

  const signal_init = async () => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
    try {
      await conn.invoke("init", token);
    } catch (err) {
      setTimeout(signal_init, 1000);
    }
  };

  const signal_update = async () => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;
    try {
      await conn.invoke("update", token);
    } catch (err) {
      setTimeout(signal_update, 1000);
    }
  };

  const signal_start = async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    await stopConnection();

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_URL_KEY}/paymenthub`)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.None)
      .build();

    connection.keepAliveIntervalInMilliseconds = 10000;
    connection.serverTimeoutInMilliseconds = 60000;

    connection.onreconnected(async () => {
      await signal_init();
      await signal_update();
    });

    try {
      await connection.start();
      connectionRef.current = connection;
      setGlobalConnection(connection);
      await signal_init();
      await signal_update();
      console.log("SignalR Connected.");
    } catch (err) {
      console.error("SignalR connection failed:", err);
      setTimeout(signal_start, 3000);
    } finally {
      isStartingRef.current = false;
    }
  };

  const setMainContent = (cryllexChains = []) => {
    return <Main
      cryllexChains={cryllexChains}
      connection={globalConnection}
      signal_update={signal_update}
      selectedToken={selectedToken}
      selectedChain={selectedChain}
      onSetNetwork={onSetNetwork}
      onSetCrypto={onSetCrypto}
      countries={countries}
      setSelectedChain={setSelectedChain}
      setSelectedToken={setSelectedToken}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  }

  const onSetNetwork = async (chain) => {
    if (!chain) { return false; }

    // Cleanup storage when switching networks
    if (previousChainRef.current && previousChainRef.current !== chain.platformType) {
      cleanupStorageByPlatform(previousChainRef.current);
      console.log(`🔄 Switched from platform ${previousChainRef.current} to ${chain.platformType}`);
    }

    const spinnerId = uuid();
    dispatch(addPageSpinner(spinnerId));
    setSelectedToken(null);

    ApiService.onSetNetwork({ networkId: chain?.id }).then(() => {
      previousChainRef.current = chain.platformType;
      setSelectedChain(chain);
      if (selectedChain) {
        window.location.reload();
      }
    }).catch(error => AlertService.alert("error", error)).finally(() => {
      dispatch(removePageSpinner(spinnerId));
    })
  }

  const onSetCrypto = async (_token) => {
    if (!_token) { return false; }
    const spinnerId = uuid();
    dispatch(addPageSpinner(spinnerId));
    ApiService.onSetCrypto({ cryptoCurrencyId: _token?.id, name: _token?.slug }).then(() => {
      setSelectedToken(_token);
      setActiveTab(3);
    }).catch(error => AlertService.alert("error", error)).finally(() => {
      dispatch(removePageSpinner(spinnerId));
    })
  }

  return (
    <PaymentDataComponent>
      {/* <AuthModal /> */}

      <div>
        {(() => {
          // Only render wallet component when chain is selected
          // This ensures SDKs are only initialized when needed
          if (!selectedChain) {
            return setMainContent(cryllexChains);
          }

          switch (selectedChain.platformType) {
            case TRON_PLATFORM_TYPE:
              return (
                <TronComponent key="tron">
                  {setMainContent(cryllexChains)}
                </TronComponent>
              )
            default:
              return (
                <WalletConnectComponent key="evm">
                  {setMainContent(cryllexChains)}
                </WalletConnectComponent>
              )
          }
        })()}
      </div>
      <Footer chains={cryllexChains} />
    </PaymentDataComponent>
  )
}
